
import {
  defineComponent, computed, reactive, onBeforeMount, inject, Ref, watch,
} from 'vue';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import Icone from '@/core/components/Icone.vue';
import ColunaGrade from '@/core/components/Tela/ColunaGrade.vue';
import { ECustomRenderRow, IColumn } from '@/core/models/AntDesign/IColumn';
import { IListaPaginadaMetaData } from '@/core/models/Consulta/IListaPaginada';
import { useGradeBase } from '@/core/composables/GradeBase';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import { IPagination } from '@/core/models/AntDesign/IPagination';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import UtilitarioDispositivo from '@/core/utilitarios/UtilitarioDispositivo';
import { IDTOVenda } from '@/models/DTO/Vendas/IDTOVenda';
import { IProcessoVendas } from '@/models/Entidades/Cadastros/Vendas/IProcessoVendas';
import storeSistema from '@/store/storeSistema';

export default defineComponent({
  name: 'VendasListagem',
  components: {
    Icone,
    ColunaGrade,
    MensagemSemDados,
  },
  props: {
    visualizacaoKanban: {
      type: Boolean,
      default: true,
    },
    vendas: {
      type: Array as () => IDTOVenda[],
      required: true,
    },
    selecionados: {
      type: Array as () => number[],
      required: true,
    },
    paginacao: {
      type: Object as () => IPagination,
      required: true,
    },
  },
  emits: ['update:vendas', 'update:visualizacaoKanban', 'update:paginacao', 'update:selecionados', 'selecaoAlterada', 'buscarVendas', 'visualizar', 'imprimir', 'imprimirNfe', 'novoLancamento'],
  setup(props, { emit }) {
    const { telaBase } = useTelaBase();
    const {
      gradeBase, defineAlturaScroll,
    } = useGradeBase();
    const processoVendas = inject('VENDAS_PROCESSO_VENDAS') as Ref<IProcessoVendas>;

    const state = reactive({
      colunas: [] as IColumn[],
      paginacaoApi: { totalRegistros: 0, paginaAtual: 1 } as IListaPaginadaMetaData,
      timerRegistros: 0,
      totalRegistrosComMascara: '100',
      telaMobile: false,
    });

    const computedVendas = computed({
      get: () => props.vendas,
      set: (valor: IDTOVenda[]) => {
        emit('update:vendas', valor);
      },
    });

    const computedVisualizacaoKanban = computed({
      get: () => props.visualizacaoKanban,
      set: (valor: boolean) => {
        emit('update:visualizacaoKanban', valor);
      },
    });

    const computedSelecionados = computed({
      get: () => props.selecionados,
      set: (valor: number[]) => {
        emit('update:selecionados', valor);
      },
    });

    const computedPaginacao = computed({
      get: () => props.paginacao,
      set: (valor: IPagination) => {
        emit('update:paginacao', valor);
      },
    });

    function preencherColunas() {
      if (state.telaMobile) {
        state.colunas = [
          {
            title: 'Código', dataIndex: 'codigo', key: 'Codigo', position: 1, visible: false,
          },
          {
            title: 'Venda', dataIndex: 'venda', key: 'Venda', position: 2, visible: true, ellipsis: true,
          },
        ];
      } else {
        state.colunas = [
          {
            title: 'PK', dataIndex: 'codigo', key: 'Codigo', position: 0, visible: false, ordering: true, customRenderRow: ECustomRenderRow.LinkAcao,
          },
          {
            title: 'Venda', dataIndex: 'numero', key: 'Numero', position: 1, visible: true, ordering: true, filtering: true, customRenderRow: ECustomRenderRow.LinkAcao, width: 80,
          },
          {
            title: 'Numero NF', dataIndex: 'numeroNotaFiscal', key: 'NumeroNotaFiscal', position: 2, visible: true, ordering: true, filtering: true, customRenderRow: ECustomRenderRow.LinkAcao, width: 100,
          },
          {
            title: 'Etapa', dataIndex: 'nomeEtapa', key: 'NomeEtapa', position: 3, visible: true, ordering: true, width: 150, align: 'center',
          },
          {
            title: 'Data Criação', dataIndex: 'dataCriacao', key: 'DataCriacao', position: 4, visible: true, ordering: true, filtering: true, customRenderRow: ECustomRenderRow.MascararData, width: 150, align: 'center',
          },
          {
            title: 'Valor Total', dataIndex: 'totalVenda', key: 'TotalVenda', position: 5, visible: true, ordering: true, customRenderRow: ECustomRenderRow.MascararFracionado, align: 'right', width: 150,
          },
          {
            title: 'Vendedor', dataIndex: 'nomeVendedor', key: 'NomeVendedor', position: 6, visible: true, ellipsis: true, ordering: true, filtering: true, width: 150,
          },
          {
            title: 'Cliente', dataIndex: 'nomeCliente', key: 'NomeCliente', position: 7, visible: true, ellipsis: true, ordering: true, filtering: true, width: 200,
          },
          {
            title: 'Ações', key: 'acoes', position: 8, visible: true, width: 100, align: 'center', fixed: 'right', customRenderRow: ECustomRenderRow.IconeAcoes,
          },
        ];
      }
    }

    async function buscarVendas() {
      emit('buscarVendas', telaBase.ordenacaoSelecionada, { ...gradeBase.filtrosCabecalho });
    }

    async function alterarQuantidadeRegistroTotal(valor: any) {
      clearTimeout(state.timerRegistros);
      let totalRegistros = 100;
      state.timerRegistros = setTimeout(async () => {
        if (UtilitarioGeral.valorValido(valor)) {
          const limiteRegistros = UtilitarioMascara.removerMascaraNumero(valor);
          if (limiteRegistros > 0) {
            totalRegistros = limiteRegistros;
          }
        }
        computedPaginacao.value.total = totalRegistros;
        state.totalRegistrosComMascara = UtilitarioMascara.mascararNumeroInteiro(totalRegistros);
        buscarVendas();
      }, 600);
    }

    async function mudarPagina(current: number, pageSize: number) {
      computedPaginacao.value.current = current;
      computedPaginacao.value.pageSize = pageSize;
      buscarVendas();
    }

    async function mostrarNovaPagina(current: number, pageSize: number) {
      computedPaginacao.value.current = 1;
      computedPaginacao.value.pageSize = pageSize;
      buscarVendas();
    }

    function mostrarTotalPaginacao(total: number, range: number[]) {
      return `${UtilitarioMascara.mascararNumeroInteiro(range[0])} - ${UtilitarioMascara.mascararNumeroInteiro(range[1])} de ${UtilitarioMascara.mascararNumeroInteiro(total)} registros`;
    }

    function keyupQuantidadeRegistroTotal(event: KeyboardEvent) {
      const alvo = event.target as HTMLInputElement | null;
      alterarQuantidadeRegistroTotal(alvo?.value ?? '');
    }

    const mudarSelecao = (selectedRowKeys: any) => {
      computedSelecionados.value = selectedRowKeys;
      emit('selecaoAlterada');
    };

    function visualizar(index: number) {
      emit('visualizar', props.vendas[index].codigo);
    }

    function imprimir(index: number) {
      emit('imprimir', props.vendas[index].codigo);
    }

    function imprimirNfe(index: number) {
      emit('imprimirNfe', props.vendas[index].codigo);
    }

    function novoLancamento() {
      emit('novoLancamento');
    }

    onBeforeMount(async () => {
      state.telaMobile = UtilitarioDispositivo.larguraTelaMobile();
      preencherColunas();
      computedPaginacao.value.total = 100;
      computedPaginacao.value.showSizeChanger = true;
      computedPaginacao.value.pageSize = 100;
      computedPaginacao.value.pageSizeOptions = ['10', '50', '100', '500', '1000'];
    });

    watch(() => storeSistema.state.layoutMobile, () => {
      preencherColunas();
    });

    return {
      state,
      storeSistema,
      props,
      window,
      telaBase,
      gradeBase,
      computedVendas,
      computedVisualizacaoKanban,
      computedSelecionados,
      computedPaginacao,
      processoVendas,
      UtilitarioMascara,
      UtilitarioData,
      mudarPagina,
      buscarVendas,
      mostrarNovaPagina,
      mostrarTotalPaginacao,
      keyupQuantidadeRegistroTotal,
      alterarQuantidadeRegistroTotal,
      mudarSelecao,
      ECustomRenderRow,
      visualizar,
      imprimir,
      novoLancamento,
      defineAlturaScroll,
      imprimirNfe,
    };
  },
});

